import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import identity from 'lodash/identity';
import sortBy from 'lodash/sortBy';

import { ApiResponse, ApiVariables } from '../../../api/apiSchema';
import { AsyncResult, Result } from '../../../core';
import { BindingProps, StoreNode } from '../../../store';
import { IChartDataSource } from '../chartSchema';
import { ApiResult, ApiRequestOptions } from '../../../api/apiSchema';
import { Store } from '../../../store/store';
import { Error, error } from '../../../core/error';
import { parseKeenJsonResult } from '../utils/parseKeenJsonResult';
import { ApiError } from '../../../api/apiError';
import { 
  TeamPerformanceChartData, 
  TeamPerformanceChartDatum, 
  TeamPerformanceChartMode, 
  TeamPerformanceChartParams, 
  TeamPerformanceChartSeriesData 
} from '.';

type Props = BindingProps<{
  params: TeamPerformanceChartParams,
  suppressFetchOnParamsChange?: boolean
}>

export class TeamPerformanceDataSource
  extends StoreNode
  implements IChartDataSource<TeamPerformanceChartSeriesData> {

  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable<this, 'handleError'>(this, {
      params: computed,
      suppressFetchOnParamsChange: computed,
      mode: computed,
      isFetching: observable,
      error: observable,
      reset: action,
      fetch: action,
      isBatchRequesting: observable,
      insertUploads: action,
      insert: action,
      handleError: action,
    });

    this.onPropChange('params',
      this.handleParamsChange);
  }

  // #region Props
  get params(): TeamPerformanceChartParams | null {
    return this.getResolvedProp('params') ?? null;
  }
  set params(val: TeamPerformanceChartParams | null) {
    this.setProp('params', val);
  }

  get suppressFetchOnParamsChange(): boolean | null {
    return this.getResolvedProp('suppressFetchOnParamsChange') ?? null;
  }
  set suppressFetchOnParamsChange(val: boolean | null) {
    this.setProp('suppressFetchOnParamsChange', val);
  }
  // #endregion

  // #region Handlers
  private handleParamsChange = () => {
    // this.fetch();
  }
  // #endregion

  get mode(): TeamPerformanceChartMode {
    return this.params?.mode ?? TeamPerformanceChartMode.Uploads;
  }

  readonly data = observable.array<TeamPerformanceChartSeriesData>([], { deep: false });

  private abortController: AbortController | null = null;

  isFetching = false;
  error: Error | null = null;

  reset() {
    this.abort();

    this.isFetching = false;
    this.error = null;
    this.abortController = null;

    this.data.clear();
  }

  async fetch(): AsyncResult {

    this.reset();

    this.isFetching = true;

    const abortCtrl = new AbortController();
    this.abortController = abortCtrl;

    const { params } = this;
    if (!params)
      return [null];

    const reqArgs: ApiVariables<'keenBatch'> = {
      args: {
        start: params.startDate?.toISO()!, // TODO: remove non-null assertion — handle missing startDate gracefully
        end: params.endDate?.toISO()!,
        timezone: params.timezone ?? undefined,
        teamId: params.teamId ?? undefined,
        limit: 10
      }
    };

    const api = this.store.api;
    const reqOpts: ApiRequestOptions = {
      signal: abortCtrl.signal
    };

    let reqPromise: ApiResult<'keenBatch'>;

    switch (params.mode) {
      default:
      case TeamPerformanceChartMode.Uploads:
        const [[res1, err1], [res2, err2]] = await Promise.all([
          api.runQuery('keen_getTeamUserUploads', reqArgs, reqOpts),
          api.runQuery('keen_getVideoAddToTeam', reqArgs, reqOpts)]
        );

        if (err1 || err2 || !res1 || !res2)
          // @ts-expect-error -- handleError accepts Error but err1/err2 may be null; guarded by condition above
          return this.handleError(err1 || err2);

        const [data, insertErr] = this.insertUploads(res1, res2);
        if (insertErr)
          return this.handleError(insertErr);

        runInAction(() => {
          this.isFetching = false;
        });

        return [data!];

      case TeamPerformanceChartMode.Edits:
        reqPromise = api.runQuery('keen_getTeamUserEdits', reqArgs, reqOpts);
        break;

      case TeamPerformanceChartMode.Bookmarks:
        reqPromise = api.runQuery('keen_getTeamUserBookmarks', reqArgs, reqOpts);
        break;

      case TeamPerformanceChartMode.Reactions:
        reqPromise = api.runQuery('keen_getTeamUserReactions', reqArgs, reqOpts);
        break;
    }

    if (!reqPromise) {
      console.error('Mode not implemented');
      return this.handleError(error('Unknown', `Invalid mode ${params.mode} was provided to TeamPerformanceDataSource`));
    }

    const [res, err] = await reqPromise;
    if (err)
      return this.handleError(err);

    const [data, insertErr] = this.insert(res!);
    if (insertErr)
      return this.handleError(insertErr);

    runInAction(() => {
      this.isFetching = false;
    });

    return [data!];
  }

  cancel(): void {
    this.abort();
  }

  isBatchRequesting = false;

  notifyBatchRequestStarted() {
    this.isBatchRequesting = true;
  }
  notifyBatchRequestCompleted() {
    this.isBatchRequesting = false;
  }

  insertUploads(res1: ApiResponse<'keenBatch'>, res2: ApiResponse<'keenBatch'>): Result<TeamPerformanceChartData> {
    // @ts-expect-error -- keenBatch response shape uses dynamic query keys not reflected in generated types
    const apiRes1: string = res1?.keenBatch.teamUserUploads?.result;
    // @ts-expect-error -- keenBatch response shape uses dynamic query keys not reflected in generated types
    const apiRes2: string = res2?.keenBatch.videoAddToTeam?.result;

    const [data, err]: Result<TeamPerformanceChartData> = getUploadsDataFromQuery(apiRes1, apiRes2);

    if (err) {
      return this.handleError(err);
    }

    this.data.replace(data ?? []);
    return [data ?? []];
  }

  insert(res: ApiResponse<'keenBatch'>): Result<TeamPerformanceChartData> {
    let apiRes: string;
    switch (this.mode) {
      default:
      case TeamPerformanceChartMode.Edits:
        // @ts-expect-error -- keenBatch response shape uses dynamic query keys not reflected in generated types
        apiRes = res.keenBatch.teamUserEdits?.result;
        break;

      case TeamPerformanceChartMode.Bookmarks:
        // @ts-expect-error -- keenBatch response shape uses dynamic query keys not reflected in generated types
        apiRes = res.keenBatch.teamUserBookmarks?.result;
        break;

      case TeamPerformanceChartMode.Reactions:
        // @ts-expect-error -- keenBatch response shape uses dynamic query keys not reflected in generated types
        apiRes = res.keenBatch.teamUserReactions?.result;
        break;
    }

    const [data, err]: Result<TeamPerformanceChartData> = getDataFromQuery(apiRes);

    if (err) {
      return this.handleError(err);
    }

    this.data.replace(data ?? []);
    return [data ?? []];
  }

  private abort() {

    const lastAbortCtrl = this.abortController;
    if (lastAbortCtrl)
      lastAbortCtrl.abort();
    this.abortController = null;
  }

  private handleError(err?: Error | string): Result {
    if (!err)
      err = new Error();
    if (typeof err === 'string')
      err = new Error('Unknown', err);

    if (err instanceof ApiError && err.type === 'RequestAborted') {
      // for aborted requests we only return the error but we don't set it on the instance and we do not repor it as error
      return [null, err];

    } else {
      console.error(`An error occured in TeamPerformanceDataSource: `, err);

      this.isFetching = false;
      this.abort();
      this.error = err;

      return [null, err];
    }
  }
}

type KeenUserItem = {
  result: number;
  'user.username'?: string;
};

function getDataFromQuery(data: string): Result<TeamPerformanceChartData> {
  const [apiResult, err] = parseKeenJsonResult<KeenUserItem[]>(data);
  if (err)
    return [null, err];

  const result: TeamPerformanceChartDatum[] = apiResult!
    .map((item) => {
      if (!item?.['user.username'])
        return null!;

      return {
        date: item?.['user.username'],
        value: item?.result ?? 0,
      }
    })
    .filter(identity);

  const outData: TeamPerformanceChartData = [{
    dataKey: ' ',
    data: sortBy(result, 'value')
  }];

  return [outData];
}

function getUploadsDataFromQuery(data1: string, data2: string): Result<TeamPerformanceChartData> {
  const [apiResult1, err1] = parseKeenJsonResult<KeenUserItem[]>(data1);
  const [apiResult2, err2] = parseKeenJsonResult<KeenUserItem[]>(data2);

  if (err1 || err2)
    return [null, err1 || err2];


  const result: TeamPerformanceChartDatum[] = apiResult1!
    .map((item) => {
      if (!item?.['user.username'])
        return null!;

      return {
        date: item?.['user.username'],
        value: item?.result ?? 0,
      }
    })
    .filter(identity);

  apiResult2?.forEach((item) => {
    const username = item['user.username'];

    if (username) {
      const r = result.find(i => i.date === username);
      if (r) {
        r.value = (Number(r.value) || 0) + item.result;
      } else {
        result.push({
          date: username,
          value: item.result || 0
        })
      }
    }
  })

  const outData: TeamPerformanceChartData = [{
    dataKey: ' ',
    data: sortBy(result, 'value')
  }];

  return [outData];
}
